import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap');

  :root {
    /* Color Palette */
    --primary-dark: #0a0a0f;
    --primary: #1a1a2e;
    --secondary: #16213e;
    
    /* Neon Colors */
    --neon-blue: #00d4ff;
    --neon-magenta: #ff0080;
    --neon-green: #00ff88;
    --neon-purple: #8b5cf6;
    --neon-yellow: #ffff00;
    
    /* Glass Colors */
    --glass-white: rgba(255, 255, 255, 0.1);
    --glass-dark: rgba(0, 0, 0, 0.2);
    --glass-blur: rgba(255, 255, 255, 0.05);
    
    /* Text Colors */
    --text-primary: #ffffff;
    --text-secondary: rgba(255, 255, 255, 0.8);
    --text-muted: rgba(255, 255, 255, 0.6);
    
    /* Spacing */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 2rem;
    --spacing-xl: 3rem;
    --spacing-xxl: 5rem;
    --spacing-xxxl: 8rem;
    
    /* Shadows */
    --shadow-glass: 0 8px 32px rgba(0, 0, 0, 0.3);
    --shadow-glass-hover: 0 12px 40px rgba(0, 0, 0, 0.4);
    --shadow-neon: 0 0 20px rgba(0, 212, 255, 0.3);
    --shadow-neon-hover: 0 0 30px rgba(0, 212, 255, 0.5);
    --shadow-inner: inset 0 1px 0 rgba(255, 255, 255, 0.1);
    
    /* Blur */
    --blur-sm: 4px;
    --blur-md: 8px;
    --blur-lg: 12px;
    --blur-xl: 16px;
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%);
    color: var(--text-primary);
    line-height: 1.6;
    overflow-x: hidden;
    min-height: 100vh;
    
    /* Animated background */
    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: 
        radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 0, 128, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(0, 255, 136, 0.05) 0%, transparent 50%);
      z-index: -1;
      animation: backgroundShift 20s ease-in-out infinite;
    }
  }
  
  @keyframes backgroundShift {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: var(--spacing-md);
    background: linear-gradient(135deg, var(--text-primary) 0%, var(--text-secondary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  h1 {
    font-size: clamp(2.5rem, 5vw, 4.5rem);
    font-weight: 900;
    letter-spacing: -0.02em;
  }
  
  h2 {
    font-size: clamp(2rem, 4vw, 3.5rem);
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  
  h3 {
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    font-weight: 700;
  }
  
  p {
    margin-bottom: var(--spacing-md);
    color: var(--text-secondary);
    line-height: 1.7;
  }
  
  a {
    text-decoration: none;
    color: var(--neon-blue);
    transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
    position: relative;
    
    &:hover {
      color: var(--neon-green);
      text-shadow: 0 0 10px currentColor;
    }
  }
  
  button {
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    border: none;
    outline: none;
    transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  /* Glass morphism base class */
  .glass {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(var(--blur-lg));
    -webkit-backdrop-filter: blur(var(--blur-lg));
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: var(--shadow-glass);
    
    &:hover {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
      box-shadow: var(--shadow-glass-hover);
      transform: translateY(-2px);
    }
  }
  
  /* Neon glow effects */
  .neon-blue {
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
    border: 1px solid rgba(0, 212, 255, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(0, 212, 255, 0.5);
      border-color: rgba(0, 212, 255, 0.5);
    }
  }
  
  .neon-magenta {
    box-shadow: 0 0 20px rgba(255, 0, 128, 0.3);
    border: 1px solid rgba(255, 0, 128, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(255, 0, 128, 0.5);
      border-color: rgba(255, 0, 128, 0.5);
    }
  }
  
  .neon-green {
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
    border: 1px solid rgba(0, 255, 136, 0.3);
    
    &:hover {
      box-shadow: 0 0 30px rgba(0, 255, 136, 0.5);
      border-color: rgba(0, 255, 136, 0.5);
    }
  }
  
  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, var(--neon-magenta) 0%, var(--neon-green) 100%);
  }
  
  /* Selection styling */
  ::selection {
    background: rgba(0, 212, 255, 0.3);
    color: var(--text-primary);
  }
  
  /* Loading animation */
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes glow {
    0%, 100% {
      text-shadow: 0 0 5px currentColor, 0 0 10px currentColor, 0 0 15px currentColor;
    }
    50% {
      text-shadow: 0 0 10px currentColor, 0 0 20px currentColor, 0 0 30px currentColor;
    }
  }
  
  /* Utility classes */
  .animate-in {
    animation: slideInUp 0.6s cubic-bezier(0.33, 1, 0.68, 1) forwards;
  }
  
  .animate-in-left {
    animation: slideInLeft 0.6s cubic-bezier(0.33, 1, 0.68, 1) forwards;
  }
  
  .animate-in-right {
    animation: slideInRight 0.6s cubic-bezier(0.33, 1, 0.68, 1) forwards;
  }
  
  .glow-text {
    animation: glow 2s ease-in-out infinite;
  }
  
  .container {
    width: 90%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 var(--spacing-md);
  }
  
  section {
    padding: var(--spacing-xxxl) 0;
    position: relative;
  }
  
  @media (max-width: 768px) {
    section {
      padding: var(--spacing-xxl) 0;
    }
    
    .container {
      width: 95%;
      padding: 0 var(--spacing-sm);
    }
  }
`;

export default GlobalStyles;